import { Box, Center, Steps, useSteps, Spinner } from "@chakra-ui/react";
import PageShell from "src/components/PageShell";
import { SectionHeader } from "src/components/ui-x";
import { useState } from "react";
import { Provider } from "../../components/ui/provider";
import GiveOffer from "../Project/GiveOffer";
import ProjectContractSetup from "./Contract";
import ProjectSignaterySetup from "./Sign";
import { SelectedContract } from "./types/SelectedContract";
import { OfferForm } from "src/pages/ProjectSetup/types/OfferObject";
import { useParams } from "react-router-dom";
import { useJobDetailQuery, useMatchedTalentQuery } from "src/lib/endpoints/hire/hire.api";
import { BasicGiveOffer } from "./BasicGiveOffer";
import { useTypedSelector } from "src/lib/store/store";
import { selectUserState } from "src/lib/features/auth/authSlice";

export default function ProjectSetupLayout() {
  const { jobId, userId } = useParams();
  const { jobDetail, isLoading } = useJobDetailQuery(jobId ?? "");

  // Fetch the matched talent to get the contractor's name
  const { matchedTalent } = useMatchedTalentQuery(jobId, userId);

  // Get the logged-in user (company/client) from Redux
  const { user } = useTypedSelector(selectUserState);

  // For company users the CLIENT in the contract is the company name;
  // fall back to firstName + lastName if no company record exists.
  const clientName =
    user?.userCompany?.name ||
    [user?.firstName, user?.lastName].filter(Boolean).join(" ");
  const contractorName = [
    matchedTalent?.matchedTalentUser?.firstName,
    matchedTalent?.matchedTalentUser?.lastName,
  ]
    .filter(Boolean)
    .join(" ");

  const [selectedContract, setSelectedContract] = useState<SelectedContract[]>(
    []
  );

  const [offerForm, setOfferForm] = useState<OfferForm>();

  const stepsStore = useSteps({
    defaultStep: 0,
    count: 3,
  });

  const onBack = () => stepsStore.goToPrevStep();

  const onNext = () => stepsStore.goToNextStep();

  if (isLoading) {
    return (
      <Center h="50vh">
        <Spinner color="primary" />
      </Center>
    );
  }

  if (!jobDetail && !isLoading) {
    return (
      <Center h="50vh">
        <Box>Job background details not found.</Box>
      </Center>
    );
  }

  const isBasic = true; // jobDetail?.payment_plan === "basic";




  const steps = [
    {
      label: "Give Offer",
      element: (
        <GiveOffer
          onNext={(data) => {
            setOfferForm(data);
            onNext();
          }}
        />
      ),
    },
    {
      label: "Contract",
      element: (
        <ProjectContractSetup
          offer={offerForm}
          onBack={onBack}
          clientName={clientName}
          contractorName={contractorName}
          onNext={(contract) => {
            setSelectedContract(contract);
            onNext();
          }}
        />
      ),
    },
    {
      label: "Sign",
      element: (
        <ProjectSignaterySetup contracts={selectedContract} offer={offerForm} onBack={onBack} />
      ),
    },
  ];
  // console.log('Selected contracts:', selectedContract)

  return (
    <Provider>
      {isBasic && jobDetail ? (
        <BasicGiveOffer
          jobDetail={jobDetail}
          isBasic={isBasic}
          talentName={contractorName}
          talent={matchedTalent?.matchedTalentUser}
        />
      ) : (
        <PageShell width="detail">
        <SectionHeader
          eyebrow="Droomwork"
          title="Set up the offer"
          description="Agree the terms, attach a contract, then sign."
        />
        <Steps.RootProvider value={stepsStore} pb={10}>
          {/* <Steps.Root defaultStep={1} count={steps.length}> */}
          <Steps.List mb={5}>
            {steps.map((step, index) => (
              <Steps.Item
                display={"flex"}
                key={index}
                index={index}
                title={step.label}
                pos="relative"
              >
                <Center flexDir={"column"} gap={2}>
                  <Steps.Indicator
                    zIndex={1}
                    bg={
                      stepsStore.value > index
                        ? "secondary"
                        : stepsStore.value == index
                        ? undefined
                        : "bg"
                    }
                    borderWidth={"thin"}
                  />
                  <Steps.Title fontWeight={"normal"}>
                    {step.label}
                  </Steps.Title>
                </Center>
                <Steps.Separator
                  pos="absolute"
                  top={5}
                  left={5}
                  flex={1}
                  bg={stepsStore.value > index ? "secondary" : undefined}
                  rounded={"full"}
                />
              </Steps.Item>
            ))}
          </Steps.List>

          {steps.map((step, index) => (
            <Steps.Content key={index} index={index}>
              {step.element}
            </Steps.Content>
          ))}
          {/* <Steps.CompletedContent>All steps are complete!</Steps.CompletedContent> */}

          {/* <ButtonGroup size="sm" variant="outline">
        <Steps.PrevTrigger asChild>
          <Button>Prev</Button>
        </Steps.PrevTrigger>
        <Steps.NextTrigger asChild>
          <Button>Next</Button>
        </Steps.NextTrigger>
      </ButtonGroup> */}
          {/* </Steps.Root> */}
        </Steps.RootProvider>
        </PageShell>
      )}
    </Provider>
  );
}
